import React, { useState, useEffect, useCallback } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { apiClient } from '../../../api'
import { baseUrl } from '../../../Environments/environment'
import { toast } from 'react-toastify'
import PaymentMethodList from '../../CreditSetup/PaymentMethodList'
import { useNavigate } from 'react-router-dom'
import { useProperty } from '../../../Contexts/PropertyContext/PropertyContext'
import CheckoutModal from './CheckoutModal'
import { formatShopAmount } from '../../CreditSetup/paymentCurrency.util'

const PaymentModal = ({propertyId, isOpen, onClose, totalAmount, orderData, onPaymentSuccess, onError }) => {
  const navigate = useNavigate();
  let { selectedPropertyId } = useProperty()
  selectedPropertyId = propertyId||selectedPropertyId;
  const [wallet, setWallet] = useState(null)
  const [methods, setMethods] = useState([])
  const [selectedMethod, setSelectedMethod] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingMethods, setLoadingMethods] = useState(true)
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false)

  const loadPaymentMethods = useCallback(async () => {
    if (!selectedPropertyId) return
    
    try {
      setLoadingMethods(true)
      const response = await apiClient.get(
        `${baseUrl}/stripe/payment-methods/${selectedPropertyId}`
      )

      setMethods(response.data.result || [])

      if (response.data.result && response.data.result.length > 0) {
        const defaultPm =
          response.data.result.find((m) => m.isDefault) ||
          response.data.result[0]

        setSelectedMethod(defaultPm._id)
      }
    } catch (err) {
      console.error(err)
      toast.error("Failed to load payment methods")
    } finally {
      setLoadingMethods(false)
    }
  }, [selectedPropertyId])

  useEffect(() => {
    const loadWallet = async () => {
      if (!selectedPropertyId) return
      
      try {
        const response = await apiClient.get(
          `${baseUrl}/stripe/wallet/${selectedPropertyId}`
        )
        setWallet(response.data.result)
        loadPaymentMethods()
      } catch (err) {
        console.error(err)
      }
    }

    if (isOpen && selectedPropertyId) {
      loadWallet()
    } else {
      // Reset when modal closes
      setMethods([])
      setSelectedMethod(null)
      setWallet(null)
    }
  }, [isOpen, selectedPropertyId, loadPaymentMethods])

  const makeDefault = async (methodId) => {
    try {
      await apiClient.put(`${baseUrl}/stripe/payment-method/default`, {
        paymentMethodId: methodId,
        propertyId: selectedPropertyId,
      })

      toast.success("Default payment method updated!")
      loadPaymentMethods() // Refresh list
    } catch (err) {
      console.error(err)
      toast.error("Failed to update default card")
    }
  }

  const createOrder = async () => {
    try {
      const propertyId = selectedPropertyId||localStorage.getItem("selectedPropertyId");
      const token = localStorage.getItem("accessToken");
      
      // Prepare request body with payment information
      const requestBody = {
        ...orderData,
        orderPayment: {
          paymentId: "ABCXYZ",
          paymentStatus: "pending",
          paymentAmount: orderData.orderAmount
        },
      }

      const response = await apiClient.post(
        `${baseUrl}/qrKitOrder/${propertyId}`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      
      if (response.status === 200 || response.status === 201) {
        console.log("Order created successfully", response.data.result)
        console.log(response.data.result.qrKitOrder._id);
        return response.data.result.qrKitOrder._id;
      }
      return false
    } catch (error) {
      console.error("Error creating order:", error)
      toast.error(error.response?.data?.message || "Failed to place order. Please try again.")
      return false
    }
  }

  const payWithSavedCard = async () => {
    if (!selectedMethod) {
      toast.error("Select a saved card or add a new one.")
      return
    }
    if (!wallet) {
      toast.error("Wallet information not available")
      return
    }
    if (!orderData) {
      toast.error("Order data is missing")
      return
    }

    setLoading(true)
    try {
      const orderId = await createOrder(orderData)
      if (!orderId) {
        toast.error("Failed to create order. Please try again.")
        // Close all modals on order creation failure
        if (onError) {
          onError()
        } else {
          onClose()
        }
        return
      }
      const amountCents = Math.round(totalAmount * 100) // Convert to cents
      
      const res = await apiClient.post(`${baseUrl}/purchaseStripe/charge-off-session`, {
        propertyId: selectedPropertyId,
        amount: amountCents,
        paymentMethodId: selectedMethod,
        customerId: wallet.stripeCustomerId,
        userId: wallet._id,
        orderId: orderId,
      })

      if (res.data.result.success) {
        toast.success("Order created successfully!")
        
        if (orderId && onPaymentSuccess) {
          onPaymentSuccess(res.data.result)
        }
        onClose()
      } else {
        toast.error("Payment failed. Please try again.")
        // Close all modals on payment failure
        if (onError) {
          onError()
        } else {
          onClose()
        }
      }
    } catch (err) {
      console.error(err)
      toast.error(err.response?.data?.message || "Payment failed. Please try again.")
      // Close all modals on error
      if (onError) {
        onError()
      } else {
        onClose()
      }
    } finally {
      setLoading(false)
    }
  }

  const payWithNewCard = () => {
    if (!wallet) {
      toast.error("Wallet information not available")
      return
    }
    if (!orderData) {
      toast.error("Order data is missing")
      return
    }
    // Open checkout modal for new card
    setIsCheckoutModalOpen(true)
  }

  const handleCheckoutSuccess = (paymentData) => {
    setIsCheckoutModalOpen(false)
    if (onPaymentSuccess) {
      onPaymentSuccess(paymentData)
    }
    onClose()
  }

  const handleCheckoutError = () => {
    // Close CheckoutModal
    setIsCheckoutModalOpen(false)
    // Close PaymentModal and PurchaseModal
    if (onError) {
      onError()
    } else {
      onClose()
    }
  }

  if (!isOpen) return null

  const currency = orderData?.currency ?? "usd"

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Payment Methods</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
            aria-label="Close modal"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loadingMethods ? (
            <div className="space-y-3">
              {[1, 2].map((i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-4 rounded-lg border border-gray-200 animate-pulse"
                >
                  <div className="space-y-2 flex-1">
                    <div className="h-5 w-32 bg-gray-200 rounded"></div>
                    <div className="h-4 w-24 bg-gray-200 rounded"></div>
                  </div>
                  <div className="h-8 w-24 bg-gray-200 rounded"></div>
                </div>
              ))}
            </div>
          ) : (
            <PaymentMethodList
              methods={methods}
              selectedId={selectedMethod}
              onSelect={setSelectedMethod}
              onMakeDefault={makeDefault}
            />
          )}

          {/* Total Amount Display */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Total Amount</span>
              <span className="text-lg font-semibold text-gray-900">
                {formatShopAmount(totalAmount, currency)}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 bg-gray-50">
          <div className="flex gap-3">
            <button
              onClick={payWithNewCard}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? "Processing..." : "Add New Card"}
            </button>

            {methods.length > 0 && currency !== "inr" && (
            <button
              onClick={payWithSavedCard}
              className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading || !selectedMethod || loadingMethods}
            >
              {loading ? "Processing..." : "Pay with saved card"}
            </button>)}
          </div>
        </div>
      </div>

      {/* Checkout Modal for New Card */}
      <CheckoutModal
        propertyId={selectedPropertyId}
        isOpen={isCheckoutModalOpen}
        onClose={() => setIsCheckoutModalOpen(false)}
        totalAmount={totalAmount}
        orderData={orderData}
        onPaymentSuccess={handleCheckoutSuccess}
        onError={handleCheckoutError}
      />
    </div>
  )
}

export default PaymentModal

